import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { emailZodSchema, type Email } from "@@/shared/email";
import {
    entityStateZodSchema,
    type EntityState,
} from "@@/shared/entity-status";
import { imageReferenceZodSchema } from "@@/shared/images/image-reference";
import { addressZodSchema, type Address } from "@@/shared/locations/address";
import { phoneTags, phoneZodSchema, type Phone } from "@@/shared/phone";
import { userIdZodSchema, type UserId } from "@@/shared/users/user-id";
import type { Branded } from "@@/utilities/branded";
import { isFuture } from "date-fns";
import type { Except } from "type-fest";
import { z } from "zod";
import { brandingIdZodSchema, type BrandingId } from "../branding/branding-id";
import { asDate } from "../date-time/as-date";
import {
    metaContentZodSchema,
    type MetaContent,
} from "../meta-content/meta-content";
import type { MetaImage } from "../meta-content/meta-image";
import { metaImageZodSchema } from "../meta-content/meta-image";
import { parseMetaContentPosterImage } from "../meta-content/parse-meta-content-poster-image";
import {
    providerGroupIdZodSchema,
    type ProviderGroupId,
} from "../provider-groups/provider-group-id";
import { storageItemImageReferenceZodSchema } from "../storage-items/storage-item-images/storage-item-image-reference";
import type { Activity } from "./activity";
import {
    companyDetailsIdZodSchema,
    type CompanyDetailsId,
} from "./company-details/company-details-id";
import {
    providerIdFactory,
    providerIdZodSchema,
    type ProviderId,
} from "./provider-id";
import {
    providerOptionsZodSchema,
    type ProviderOptions,
} from "./provider-options";

type ProviderSlug = Branded<string, "ProviderSlug">;
type ProviderIdOrSlug = ProviderId | ProviderSlug;
const alfredProviderId = "provider-alfred" as ProviderId;

type Provider = {
    readonly _id: ProviderId;
    readonly _type: "PROVIDER";
    readonly _status: EntityState;
    readonly _createdAt: IsoAndUnixTimestamp;

    readonly groupId?: ProviderGroupId;
    readonly meta: MetaContent;
    readonly slug: ProviderSlug;
    readonly legacySlugs?: ProviderSlug[];
    readonly logoImageRef?: MetaImage;
    /**
     * @deprecated Use scope with owner permission instead ("providerId:owner"), moving away from array of ids, there might exist providers that hasn't migrated to owner permission yet though so check both for the time beeing
     */
    readonly ownerIds: UserId[];
    readonly emails: Email[];
    readonly phones: Phone[];
    readonly address: Address;
    readonly activities?: Activity[];
    readonly options: ProviderOptions;
    readonly brandingId?: BrandingId;
    readonly companyDetailsId?: CompanyDetailsId;
};

const providerSlugZodSchema = z
    .string()
    .min(1) as unknown as z.ZodSchema<ProviderSlug>;
const providerIdOrSlugZodSchema = z.string().refine(value => {
    if (value.startsWith("provider")) return providerIdZodSchema.parse(value);
    return providerSlugZodSchema.parse(value);
}) as unknown as z.ZodSchema<ProviderIdOrSlug>;
const providerZodSchema: z.ZodSchema<Provider> = z.object({
    _id: providerIdZodSchema,
    _type: z.literal("PROVIDER"),
    _status: entityStateZodSchema,
    _createdAt: isoAndUnixZodSchema,
    groupId: providerGroupIdZodSchema.optional(),
    meta: metaContentZodSchema,
    slug: providerSlugZodSchema,
    legacySlugs: z.array(providerSlugZodSchema).optional(),
    logoImageRef: metaImageZodSchema.optional(),
    ownerIds: z.array(userIdZodSchema),
    emails: z.array(emailZodSchema),
    phones: z.array(phoneZodSchema),
    address: addressZodSchema,
    activities: z.undefined().optional(),
    options: providerOptionsZodSchema,
    brandingId: brandingIdZodSchema.optional(),
    companyDetailsId: companyDetailsIdZodSchema.optional(),
});

type FactoryParams = Except<
    Provider,
    "_id" | "_type" | "_createdAt" | "_status"
>;

const providerFactory = (
    params: FactoryParams & {
        _id?: ProviderId;
        _status?: EntityState;
    },
): Provider => {
    return {
        ...params,
        _id: params._id || providerIdFactory(),
        _type: "PROVIDER",
        _createdAt: nowIsoAndUnix(),
        _status: params._status || "ACTIVE",
    };
};

/**
 * @deprecated All images in meta.images on a provider
 * should be poster images, so this function is not needed
 * it's only here for backwards compatibility, selects
 * first image in image array and returns it.
 */
const parseProviderPosterImage = (provider: Provider | undefined) => {
    if (typeof provider === "undefined") return undefined;
    return parseMetaContentPosterImage(provider?.meta);
};

const getProviderPrepTime = (provider: Provider) => {
    if (
        provider.options.prepTimeOverride &&
        isFuture(asDate(provider.options.prepTimeOverride.until))
    ) {
        return provider.options.prepTimeOverride.prepTime;
    }
    return provider.options.prepTime;
};

const getPublicPhoneNumberForProvider = (provider: Provider) => {
    const phoneNumber = provider?.phones.find(num =>
        num.categoryTags?.includes(phoneTags.public.tag),
    )?.phoneNumber;
    if (!phoneNumber) {
        // eslint-disable-next-line no-console
        console.error(
            "Missing public phone number for provider: " +
                provider.slug +
                ", " +
                provider._id,
        );
    }
    return phoneNumber;
};

const isProviderCompleteZodSchema: z.ZodSchema = z.object({
    _id: providerIdZodSchema,
    _type: z.literal("PROVIDER"),
    _status: entityStateZodSchema,
    _createdAt: isoAndUnixZodSchema,
    groupId: providerGroupIdZodSchema.nullish(),
    meta: metaContentZodSchema,
    slug: providerSlugZodSchema,
    logoImageRef: z
        .union([imageReferenceZodSchema, storageItemImageReferenceZodSchema])
        .optional(),
    ownerIds: z.array(userIdZodSchema),
    emails: z.array(emailZodSchema),
    phones: z.array(phoneZodSchema),
    brandingIdZodSchema: brandingIdZodSchema.optional(),
    companyDetailsId: companyDetailsIdZodSchema,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isProvider = (item: any): item is Provider => {
    return item._type === "PROVIDER";
};

export {
    alfredProviderId,
    getProviderPrepTime,
    getPublicPhoneNumberForProvider,
    isProvider,
    isProviderCompleteZodSchema,
    parseProviderPosterImage,
    providerFactory,
    providerIdOrSlugZodSchema,
    providerSlugZodSchema,
    providerZodSchema,
};
export type { Provider, ProviderIdOrSlug, ProviderSlug };
